import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import { Maybe } from "helpers/maybeFunctor";
import { isValidColor, toHexColor } from "helpers/colorFormat";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";

const BrandSettingsForm = ({setGlobalSettings, globalSettings}) => {
  const [globalMessage, setGlobalMessage] = useState("");
  const [brandPostingInstructions, setBrandPostingInstructions] = useState("");
  const [isAuto, setIsAuto] = useState(true);
  const [useBrandProfile, setUseBrandProfile] = useState(true);
  const [contentType, setContentType] = useState("");
  const [language, setLanguage] = useState("");
  // Multi-language: full list of publish languages (full-name values). First =
  // primary, kept in sync with `language`. Single entry = single-language brand.
  const [contentLanguages, setContentLanguages] = useState([]);
  const [brandPostingCadence, setBrandPostingCadence] = useState("");
  const [supervisors, setSupervisors] = useState([]);
  const [modelType, setModelType] = useState('');
  const [supervisor, setSupervisor] = useState({
    label: '',
    value: '',
  });
  const [secondaryTheme, setSecondaryTheme] = useState("");
  const [themeName, setThemeName] = useState("");
  const [themeError, setThemeError] = useState(false);
  const primaryColorPickerRef = useRef(null);
  const secondaryColorPickerRef = useRef(null);
  const [isSupervisorToggle, setIsSupervisorToggle] = useState(false);
  const [isSupervisorsToggle, setIsSupervisorsToggle] = useState(false);
  const [postingTime, setPostingTime] = useState("");

  const contentTypeOptions = [
    { value: "text", label: "Text" },
    { value: "image", label: "Image" },
    { value: "text_image", label: "Text + Image" },
    // Permissive: attach any of text / image / video and each platform takes
    // the richest it can use (video > image > text). A platform that can use
    // none of what is attached is skipped, not failed — which is how one post
    // serves YouTube (video only), Pinterest (needs media) and X (text) at once.
    { value: "text_image_video", label: "Text + Image + Video (any combination)" },
  ];

  const usersRedux = useSelector((s) => s.user.users);
  const userInfoRedux = useSelector((s) => s.user.userInfo);

  const {
    register,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const Filtersupervisors = Maybe.of(usersRedux).map((users) => {
    return users;
    // return users.filter((user) => user.email !== emailRedux);
  });

  const supervisorsOptions = Filtersupervisors?.value?.map((i) => ({
    label: i.name,
    value: i._id,
  }));

  const languageOptions = [
    { value: "english", label: "English" },
    { value: "spanish", label: "Español" },
    { value: "french", label: "Français" },
    { value: "german", label: "Deutsch" },
    { value: "italian", label: "Italiano" },
    { value: "portuguese", label: "Português" },
    { value: "dutch", label: "Nederlands" },
    { value: "polish", label: "Polski" },
    { value: "russian", label: "Русский" },
    { value: "japanese", label: "日本語" },
    { value: "korean", label: "한국어" },
    { value: "chinese", label: "中文" },
    { value: "arabic", label: "العربية" },
    { value: "hindi", label: "हिन्दी" },
    { value: "turkish", label: "Türkçe" },
    { value: "indonesian", label: "Bahasa Indonesia" },
    { value: "vietnamese", label: "Tiếng Việt" },
    { value: "thai", label: "ไทย" },
  ];

  useEffect(() => {
    if (globalSettings) {
      setSupervisors(
        supervisorsOptions?.filter((x) =>
         globalSettings?.listEmailSupervisors.includes(x.value)
        )
      );
      setSupervisor(
        supervisorsOptions?.find(
          (x) => x.value === globalSettings?.mainEmailSupervisor
        )
      );
      setIsAuto(globalSettings?.autopost);
      // useBrandProfile defaults to true if missing (older brands didn't have it).
      setUseBrandProfile(globalSettings?.useBrandProfile !== false);
      setLanguage(globalSettings?.language);
      setContentLanguages(
        Array.isArray(globalSettings?.contentLanguages) && globalSettings.contentLanguages.length
          ? globalSettings.contentLanguages
          : (globalSettings?.language ? [globalSettings.language] : ["english"])
      );
      setBrandPostingInstructions(
       globalSettings?.brandPostingInstructions
      );
      setGlobalMessage(globalSettings?.globalBrandMessageForAudience);
      setThemeName(globalSettings?.brandThemePrimaryColor);
      setSecondaryTheme(globalSettings?.brandThemeSecondaryColor);
      setContentType(globalSettings?.postingType);
      setBrandPostingCadence(globalSettings?.postingCadence);
      setModelType(globalSettings?.modelType);
      setBrandPostingCadence(globalSettings?.postingCadence);
      setModelType(globalSettings?.modelType);
      setPostingTime(globalSettings?.postingTime);
    }
  }, [globalSettings]);

  return (
    <>
      <div className="form-02">
        <div className="d-flex justify-content-between gap-2">
          <div className="form-group mt-3 w-50 position-relative align-items-center">
            <label htmlFor="" className="f-14 inter text-black font-weight-500">
              Primary Brand Theme Color <span className="text-danger">*</span>
            </label>

            {/* Hidden Color Picker Input. The native swatch only accepts a
                6-digit hex, so feed it a normalised value while the text box
                keeps whatever hex/rgba string was typed or pasted. */}
            <input
              type="color"
              className="w-12 h-10 hidden color-input border rounded-md cursor-pointer"
              value={toHexColor(themeName)}
              ref={primaryColorPickerRef}
              onChange={(e) => {
                setThemeName(e.target.value);
                setGlobalSettings({ ...globalSettings, brandThemePrimaryColor: e.target.value });
                setThemeError(false);
              }}
            />

            {/* Text Input with Attached Color Display. Editable so the hex /
                rgba code can be copied/pasted alongside the picker swatch. */}
            <div className="d-flex align-items-center w-100 position-relative">
              <input
                type="text"
                className="form-control pe-5"
                placeholder="#1d4ed8 or rgba(29,78,216,1)"
                value={themeName}
                {...register("theme", {
                  required: "Brand theme is required",
                  maxLength: 30,
                  validate: (v) => !v || isValidColor(v) || "Enter a valid hex or rgb(a) color",
                  onChange: (e) => {
                    setThemeName(e.target.value);
                    setGlobalSettings({ ...globalSettings, brandThemePrimaryColor: e.target.value });
                    if (e.target.value.length > 0) {
                      setThemeError(false);
                    } else {
                      setThemeError(true);
                    }
                  },
                })}
              />
              <div
                id="colored"
                className="position-absolute end-0 me-2 border rounded"
                style={{
                  background: themeName ?? "white",
                  width: "25px",
                  height: "25px",
                  cursor: "pointer",
                }}
                onClick={() => primaryColorPickerRef.current.click()}
              ></div>
            </div>

            {/* Validation Error Message */}
            {themeError && (
              <p role="alert" className="validation-error-msg">
                Please add primary brand theme color!
              </p>
            )}
          </div>
          <div className="form-group mt-3 w-50 position-relative align-items-center">
            <label htmlFor="" className="f-14 inter text-black font-weight-500">
              Secondary Brand Theme Color <span className="text-danger">*</span>
            </label>

            {/* Hidden Color Picker Input. Feed the native swatch a normalised
                6-digit hex; the text box holds the raw hex/rgba string. */}
            <input
              type="color"
              ref={secondaryColorPickerRef}
              className="color-input w-12 h-10 hidden border rounded-md"
              value={toHexColor(secondaryTheme)}
              onChange={(e) => {
                setSecondaryTheme(e.target.value);
                setGlobalSettings({ ...globalSettings, brandThemeSecondaryColor: e.target.value });
              }}
            />

            {/* Text Input with Attached Color Display. Editable so the hex /
                rgba code can be copied/pasted alongside the picker swatch. */}
            <div className="d-flex align-items-center w-100 position-relative">
              <input
                type="text"
                className="form-control pe-5"
                placeholder="#0ea5e9 or rgba(14,165,233,1)"
                value={secondaryTheme}
                {...register("secondaryTheme", {
                  required: "Secondary Brand theme is required",
                  maxLength: 30,
                  validate: (v) => !v || isValidColor(v) || "Enter a valid hex or rgb(a) color",
                  onChange: (e) => {
                    setSecondaryTheme(e.target.value);
                    setGlobalSettings({ ...globalSettings, brandThemeSecondaryColor: e.target.value });
                  },
                })}
              />
              <div
                id="colored"
                className="position-absolute end-0 me-2 border rounded"
                style={{
                  background: secondaryTheme ?? "white",
                  width: "25px",
                  height: "25px",
                  cursor: "pointer",
                }}
                onClick={() => secondaryColorPickerRef.current.click()}
              ></div>
            </div>
          </div>
        </div>

        <div className="form-check form-switch ml-1 mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="flexSwitchCheckChecked"
            value={isSupervisorToggle}
            onChange={(e) => setIsSupervisorToggle(e.target.checked)}
            checked={isSupervisorToggle}
          />
          <label
            className="form-check-label ml-2 f-14 inter text-black font-weight-500"
            htmlFor="flexSwitchCheckChecked"
          >
            Enable Main Supervisor
          </label>
        </div>
        <div className="form-group-div mt-3">
          <label className="f-14 inter text-black font-weight-500">
            Main Supervisor
          </label>
          <Select
            isDisabled={!isSupervisorToggle}
            value={supervisor}
            options={supervisorsOptions}
            onChange={(e) => {setSupervisor(e); setGlobalSettings({ ...globalSettings, mainEmailSupervisor: e.value });}}
          />
        </div>
        <div className="form-check form-switch ml-1 mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="flexSwitchCheckChecked"
            value={isSupervisorsToggle}
            onChange={(e) => setIsSupervisorsToggle(e.target.checked)}
            checked={isSupervisorsToggle}
          />
          <label
            className="form-check-label ml-2 f-14 inter text-black font-weight-500"
            htmlFor="flexSwitchCheckChecked"
          >
            Enable list of Supervisors
          </label>
        </div>
        <div className="form-group-div mt-3">
          <label className="f-14 inter text-black font-weight-500">
            List of Supervisors
          </label>
          <Select
            isDisabled={!isSupervisorsToggle}
            options={supervisorsOptions?.filter(
              (x) => x.value !== supervisor?.value ?? ""
            )}
            isMulti
            value={supervisors}
            onChange={(options) => {setSupervisors(options); setGlobalSettings({ ...globalSettings, listEmailSupervisors: options.value });}}
          />
        </div>

        {/* Email sender identity + signature. Fed into generated email
            copy and auto-appended to every outbound email at send time. */}
        <div className="form-group-div mt-4">
          <label className="f-14 inter text-black font-weight-600">
            Email sender &amp; signature
          </label>
          <div className="text-muted f-12 mb-2">
            Used as the sender identity in generated emails and appended as the signature on every outbound email.
          </div>
          <div className="row">
            {[
              { key: 'ownerName', label: 'Owner name', ph: 'Alex Rivera' },
              { key: 'ownerTitle', label: 'Title', ph: 'Founder' },
              { key: 'ownerEmail', label: 'Contact email', ph: 'alex@brand.com' },
              { key: 'ownerPhone', label: 'Phone (optional)', ph: '+1 555 0100' },
              { key: 'companyWebsite', label: 'Website', ph: 'https://brand.com' },
              { key: 'replyToAddress', label: 'Reply-To (optional)', ph: 'replies@brand.com' },
            ].map((f) => (
              <div className="col-md-6 mb-2" key={f.key}>
                <label className="f-12 inter text-black font-weight-500">{f.label}</label>
                <input
                  className="form-control"
                  type="text"
                  placeholder={f.ph}
                  value={globalSettings?.[f.key] || ''}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, [f.key]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <label className="f-12 inter text-black font-weight-500 mt-2">
            Signature block (optional — tokens: {'{{ownerName}}'}, {'{{ownerTitle}}'}, {'{{companyName}}'}, {'{{ownerEmail}}'}, {'{{ownerPhone}}'}, {'{{companyWebsite}}'})
          </label>
          <textarea
            className="form-control"
            rows={4}
            placeholder={"Leave blank to auto-build from the fields above, e.g.\n{{ownerName}}, {{ownerTitle}}\n{{companyName}}\n{{ownerEmail}}"}
            value={globalSettings?.emailSignature || ''}
            onChange={(e) => setGlobalSettings({ ...globalSettings, emailSignature: e.target.value })}
          />
        </div>

        {/* Auto Generate: whether the brand produces content at all. Independent
            of Auto Post (auto-publish). Defaults to on for legacy brands that
            never had the field. */}
        <div className="form-check form-switch ml-1 mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="flexSwitchAutoGenerate"
            checked={globalSettings?.autoGenerate !== false}
            onChange={(e) => setGlobalSettings({ ...globalSettings, autoGenerate: e.target.checked })}
          />
          <label
            className="form-check-label ml-2 f-14 inter text-black font-weight-500"
            htmlFor="flexSwitchAutoGenerate"
          >
            Auto Generate
          </label>
        </div>

        <div className="form-check form-switch ml-1 mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="flexSwitchCheckChecked"
            value={isAuto}
            onChange={(e) => {setIsAuto(e.target.checked); setGlobalSettings({...globalSettings, autopost: e.target.checked});}}
            checked={isAuto}
          />
          <label
            className="form-check-label ml-2 f-14 inter text-black font-weight-500"
            htmlFor="flexSwitchCheckChecked"
          >
            Auto Post
          </label>
        </div>

        {/* Auto Newsletter: opt the brand into a fully self-provisioned
            newsletter. When on, the worker stands up a subscriber list, an
            HTML template, an embeddable signup widget, and a recurring
            weekly/monthly schedule — no manual setup. */}
        <div className="form-check form-switch ml-1 mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="flexSwitchAutoNewsletter"
            checked={globalSettings?.autoNewsletter === true}
            onChange={(e) => setGlobalSettings({ ...globalSettings, autoNewsletter: e.target.checked })}
          />
          <label
            className="form-check-label ml-2 f-14 inter text-black font-weight-500"
            htmlFor="flexSwitchAutoNewsletter"
          >
            Auto Newsletter
          </label>
        </div>

        {globalSettings?.autoNewsletter === true && (
          <div className="form-group-div mt-2 ml-1">
            <label className="f-14 inter text-black font-weight-500">
              Newsletter frequency
            </label>
            <select
              className="form-control"
              value={globalSettings?.autoNewsletterFrequency || 'weekly'}
              onChange={(e) => setGlobalSettings({ ...globalSettings, autoNewsletterFrequency: e.target.value })}
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        )}

        {/* Content alignment: canonical reference content + hard rules. EVERY
            generated artifact (posts + per-platform variants, FAQ answers,
            SEO articles, email sequences, chat replies, ideas) is verified
            and transformed against this during the Perfectionist pass —
            claims/citations/terminology are corrected to match the alignment
            content (it wins over the draft) and every rule must hold. */}
        <div className="form-group-div mt-3">
          <label className="f-14 inter text-black font-weight-500">
            Content alignment &amp; references
          </label>
          <textarea
            className="form-control"
            rows={6}
            placeholder="Paste the canonical content everything must align with: product facts, positioning, terminology, scripture/citation standards, pricing, claims that are allowed…"
            value={(globalSettings?.contentReferences && globalSettings.contentReferences[0] && globalSettings.contentReferences[0].content) || ''}
            onChange={(e) => setGlobalSettings({
              ...globalSettings,
              contentReferences: e.target.value.trim()
                ? [{ label: 'Brand alignment content', content: e.target.value }]
                : [],
            })}
          />
          <label className="f-14 inter text-black font-weight-500 mt-2">
            Content rules (one per line)
          </label>
          <textarea
            className="form-control"
            rows={4}
            placeholder={"Never promise a refund window longer than 30 days\nAlways cite the verse number when quoting\nNever mention competitor names"}
            value={Array.isArray(globalSettings?.contentRules) ? globalSettings.contentRules.join('\n') : ''}
            onChange={(e) => setGlobalSettings({
              ...globalSettings,
              contentRules: e.target.value.split('\n').map((r) => r.trim()).filter(Boolean).length
                ? e.target.value.split('\n')
                : [],
            })}
          />
          {/* The rules above are prose the model is ASKED to obey while it
              drafts. These two are literal phrases CHECKED after the fact,
              once the humanizer's learned pattern store and its polish pass
              have each rewritten the draft. That store is trained on the copy
              you are replacing, so it quietly drags new positioning back to
              the old wording — no price or link moves, so nothing else
              catches it. Applied by the lanes that reply in public. */}
          <label className="f-14 inter text-black font-weight-500 mt-2">
            Protected wording (one per line)
          </label>
          <textarea
            className="form-control"
            rows={3}
            placeholder={"community\ndone with you\nflat fee"}
            value={Array.isArray(globalSettings?.protectedPhrases) ? globalSettings.protectedPhrases.join('\n') : ''}
            onChange={(e) => setGlobalSettings({
              ...globalSettings,
              // Store the raw lines, exactly as contentRules does: trimming
              // here would swallow the newline the moment it is typed.
              // copyVocabulary trims and drops blanks on the worker side.
              protectedPhrases: e.target.value.split('\n').map((r) => r.trim()).filter(Boolean).length
                ? e.target.value.split('\n')
                : [],
            })}
          />
          <small className="text-muted f-12 inter">
            Words a rewrite may never drop. Only enforced when the draft already used them.
          </small>
          <label className="f-14 inter text-black font-weight-500 mt-2">
            Retired wording (one per line)
          </label>
          <textarea
            className="form-control"
            rows={3}
            placeholder={"AI workers\nagency\ndone for you"}
            value={Array.isArray(globalSettings?.bannedPhrases) ? globalSettings.bannedPhrases.join('\n') : ''}
            onChange={(e) => setGlobalSettings({
              ...globalSettings,
              // Store the raw lines, exactly as contentRules does: trimming
              // here would swallow the newline the moment it is typed.
              // copyVocabulary trims and drops blanks on the worker side.
              bannedPhrases: e.target.value.split('\n').map((r) => r.trim()).filter(Boolean).length
                ? e.target.value.split('\n')
                : [],
            })}
          />
          <small className="text-muted f-12 inter">
            Old positioning a rewrite may never reintroduce. Quoting someone who used it first is still allowed.
          </small>
        </div>

        {/* Auto Email Ingestion: per-brand kill-switch for INBOUND email
            ingestion. Strictly opt-in — when off, the worker stops polling
            this brand's inboxes, closes their IMAP IDLE connections, and
            queued ingestion jobs no-op, so classification / auto-reply /
            CRM analysis get no new mail until it's switched back on. */}
        <div className="form-check form-switch ml-1 mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="flexSwitchAutoEmailIngestion"
            checked={globalSettings?.autoEmailIngestion === true}
            onChange={(e) => setGlobalSettings({ ...globalSettings, autoEmailIngestion: e.target.checked })}
          />
          <label
            className="form-check-label ml-2 f-14 inter text-black font-weight-500"
            htmlFor="flexSwitchAutoEmailIngestion"
          >
            Auto Email Ingestion
          </label>
        </div>

        {isAuto && (
          <div className="form-group-div mt-3">
            <label className="f-14 inter text-black font-weight-500">
              Select Time
            </label>
            <input
              type="time"
              id="time"
              name="time"
              value={postingTime}
              onChange={(e) => {setPostingTime(e.value); setGlobalSettings({ ...globalSettings, postingTime: e.value });}}
            />
          </div>
        )}

        {/* Toggle: drive idea/post generation from the synthesized BrandProfile
            (default ON) or fall back to the raw brand fields below (OFF).
            Useful when the AI synthesis drifts and the owner wants direct control. */}
        <div className="form-check form-switch ml-1 mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="useBrandProfileSwitch"
            value={useBrandProfile}
            onChange={(e) => {
              setUseBrandProfile(e.target.checked);
              setGlobalSettings({ ...globalSettings, useBrandProfile: e.target.checked });
            }}
            checked={useBrandProfile}
          />
          <label
            className="form-check-label ml-2 f-14 inter text-black font-weight-500"
            htmlFor="useBrandProfileSwitch"
          >
            Use AI-synthesized Brand Profile
            <small className="d-block text-muted f-12">
              {useBrandProfile
                ? "Idea + post generation reads from the enriched brand profile (style, tone, big idea, content pillars, top posts)."
                : "Generation falls back to the raw brand fields below — voice, keywords, hashtags, topics."}
            </small>
          </label>
        </div>

        <div className="form-group-div mt-3">
          <label className="f-14 inter text-black font-weight-500">
            Content Languages <span className="f-12 text-muted">(first is primary; all content is generated in the primary and translated into the rest)</span>
          </label>
          <Select
            isMulti
            options={languageOptions}
            value={languageOptions.filter((option) => contentLanguages.includes(option.value))}
            onChange={(arr) => {
              const vals = (arr || []).map((x) => x.value);
              setContentLanguages(vals);
              setLanguage(vals[0] || "");
              setGlobalSettings({ ...globalSettings, contentLanguages: vals, language: vals[0] || "" });
            }}
          />
        </div>

        <div className="form-group-div mt-3">
          <label className="f-14 inter text-black font-weight-500">
            Posting Content Type
          </label>
          <Select
            options={contentTypeOptions}
            value={contentTypeOptions.find(
              (option) => option.value === contentType
            )}
            onChange={(e) => {setContentType(e.value); setGlobalSettings({ ...globalSettings, postingType: e.value });}}
          />
        </div>

        <div className="d-flex justify-content-between gap-2">
          <div className="form-group mt-3 w-50">
            <label htmlFor="" className="f-14 inter text-black font-weight-500">
              Brand Posting Cadence <span className="text-danger">*</span>
            </label>
            <div className="form-radio">
              <input
                type="radio"
                id="everyday"
                name="everyday"
                value="everyday"
                checked={brandPostingCadence === "everyday"}
                onChange={(e) => {
                  setBrandPostingCadence(e.target.value);
                  setGlobalSettings({ ...globalSettings, postingCadence: e.target.value });
                }}
              />
              <label htmlFor="everyday">Everyday</label> {" "}
              <input
                type="radio"
                id="week"
                name="week"
                value="week"
                checked={brandPostingCadence === "week"}
                onChange={(e) => {
                  setBrandPostingCadence(e.target.value);
                  setGlobalSettings({ ...globalSettings, postingCadence: e.target.value });
                }}
              />
              <label htmlFor="week">1 in week</label> {" "}
              <input
                type="radio"
                id="month"
                name="month"
                value="month"
                checked={brandPostingCadence === "month"}
                onChange={(e) => {
                  setBrandPostingCadence(e.target.value);
                  setGlobalSettings({ ...globalSettings, postingCadence: e.target.value });
                }}
              />
              <label htmlFor="month">1 in month</label>
            </div>
          </div>

          <div className="form-group mt-3 w-50">
            <label className="f-14 inter text-black font-weight-500">
              AI Model Brand Posting Type <span className="text-danger">*</span>
            </label>
            <div className="radio-margin">
              <input
                type="radio"
                id="person"
                name="modelType"
                value="Person"
                checked={modelType === "Person"}
                onChange={(e) => {setModelType(e.target.value); setGlobalSettings({ ...globalSettings, modelType: e.target.value });}}
              />
              <label htmlFor="person">Person</label>
              <input
                type="radio"
                id="product"
                name="modelType"
                value="Product"
                checked={modelType === "Product"}
                onChange={(e) => {setModelType(e.target.value); setGlobalSettings({ ...globalSettings, modelType: e.target.value });}}
              />
              <label htmlFor="product">Product</label>
              <input
                type="radio"
                id="concept"
                name="modelType"
                value="Concept"
                checked={modelType === "Concept"}
                onChange={(e) => {setModelType(e.target.value); setGlobalSettings({ ...globalSettings, modelType: e.target.value });}}
              />
              <label htmlFor="concept">Concept</label>
            </div>
          </div>
        </div>
        <div className="form-group mt-3">
          <label htmlFor="" className="f-14 inter text-black font-weight-500">
            Brand Posting Instructions
          </label>
          <textarea
            onChange={(e) => {setBrandPostingInstructions(e.target.value); setGlobalSettings({ ...globalSettings, brandPostingInstructions: e.target.value });}}
            type="text"
            value={brandPostingInstructions}
            className="form-control"
            placeholder="Brand Posting Instructions"
          ></textarea>
        </div>
        <div className="form-group mt-3">
          <label htmlFor="" className="f-14 inter text-black font-weight-500">
            Global Brand Message For Audience
          </label>
          <textarea
            onChange={(e) => {setGlobalMessage(e.target.value); setGlobalSettings({ ...globalSettings, globalBrandMessageForAudience: e.target.value }); }}
            type="text"
            className="form-control"
            value={globalMessage}
            placeholder="Global Brand Message For Audience"
          ></textarea>
        </div>

        <div className="form-group mt-4">
          <label className="f-14 inter text-black font-weight-600">Chat widget</label>
          <div className="f-12 text-muted mb-2">Powers the AI support agent embedded on this brand's site.</div>
          <label htmlFor="" className="f-14 inter text-black font-weight-500">Chat agent names</label>
          <input
            type="text"
            className="form-control"
            placeholder="Ashley, Jason, Megan"
            value={(globalSettings?.chatAgentNames || []).join(', ')}
            onChange={(e) => setGlobalSettings({ ...globalSettings, chatAgentNames: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          />
          <div className="f-12 text-muted mt-1">Comma-separated. The bot picks one per conversation as its name. Leave blank for the defaults (Ashley, Jason, Megan).</div>
        </div>
        <div className="form-group mt-3">
          <label htmlFor="" className="f-14 inter text-black font-weight-500">Chat audience</label>
          <select
            className="form-control"
            value={globalSettings?.chatAudienceDefault || 'auto'}
            onChange={(e) => setGlobalSettings({ ...globalSettings, chatAudienceDefault: e.target.value })}
          >
            <option value="auto">Auto — detect from the page (app = support, site = sales)</option>
            <option value="user">Always treat as customers (support mode)</option>
            <option value="prospect">Always treat as prospects (sales mode)</option>
          </select>
          <div className="f-12 text-muted mt-1">Overrides how the bot greets visitors. "Auto" reads the page URL: an app.* subdomain is an existing customer; the marketing site is a prospect.</div>
        </div>
      </div>
    </>
  );
};

export default BrandSettingsForm;
